import { DropOverlay } from "@/components/common/DropOverlay/DropOverlay"
import { PermissionGate } from "@/components/common/PermissionGate"
import { toast } from "@/components/common/Toast"
import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { IconLoader2 } from "@tabler/icons-react"
import { UploadCloud } from "lucide-react"
import { useNavigate } from "react-router"
import { ModuleHeader } from "../shared/ModuleHeader"
import { InboxView } from "./InboxView"
import {
  customerRequestConfirmPath,
  customerRequestDetailPath,
  customerRequestImportPath,
  customerRequestProcessPath,
} from "./routes"
import {
  CUSTOMER_REQUEST_DROP_EXTENSIONS,
  useCustomerRequestUpload,
} from "./useCustomerRequestUpload"

export function CustomerRequestsModule() {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const { hasPermission } = useUser()
  const canUpload = hasPermission(PERMISSIONS.MC_QUESTIONNAIRES_PROCESS)
  // Drag a request onto the OVERVIEW and it imports right here: same upload
  // as the import page, landing on the confirm step. Container-scoped like
  // the declarations overview, so the app chrome is never the drop target.
  const { uploadFile, isPending } = useCustomerRequestUpload()

  return (
    <div
      data-agent-target="outreach.request"
      className="relative flex min-h-full flex-col gap-6"
    >
      {canUpload && !isPending && (
        <DropOverlay
          scope="container"
          acceptedExtensions={CUSTOMER_REQUEST_DROP_EXTENSIONS}
          hint={translate("MC_Q_DROP_HINT")}
          onFilesDropped={(files) => void uploadFile(files[0])}
          onRejectedFiles={() =>
            toast.error(translate("MC_Q_DROP_UNSUPPORTED"))
          }
        />
      )}
      <ModuleHeader
        title={translate("MC_Q_TITLE")}
        helpArticle={{
          categoryId: "customer-studio",
          articleId: "customer.requests",
        }}
        subtitle={translate("MC_Q_SUBTITLE")}
        right={
          <PermissionGate permission={PERMISSIONS.MC_QUESTIONNAIRES_PROCESS}>
            <Button
              size="sm"
              className="bg-yellow1 text-black hover:bg-yellow2"
              disabled={isPending}
              onClick={() => navigate(customerRequestImportPath)}
            >
              {isPending ? (
                <IconLoader2 size={14} className="mr-1.5 animate-spin" />
              ) : (
                <UploadCloud size={14} className="mr-1.5" />
              )}
              {isPending
                ? translate("MC_Q_UPLOAD_PROCESSING")
                : translate("MC_Q_BTN_RECEIVE")}
            </Button>
          </PermissionGate>
        }
      />

      <InboxView
        onOpen={(row) => {
          // needs_confirm + extracting + extraction_failed all belong to the
          // intake step — ConfirmPage handles the poll/loading/error UI for
          // rows the user hasn't finished intaking yet.
          const isIntakeStep =
            row.status === "needs_confirm" ||
            row.status === "extracting" ||
            row.status === "extraction_failed"
          if (isIntakeStep) {
            navigate(customerRequestConfirmPath(row.id))
            return
          }
          // Once Phase A has started, processingStatus drives which of the
          // 4 process steps to land on — go straight there instead of the
          // record page's "Start/Continue processing" button.
          navigate(
            row.processingStatus !== null
              ? customerRequestProcessPath(row.id)
              : customerRequestDetailPath(row.id),
          )
        }}
      />
    </div>
  )
}
